import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import React, { useCallback, useMemo, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
import kebabCase from 'lodash/kebabCase';
import { useIntl } from 'react-intl-next';
import { IconButton } from '@atlaskit/button/new';
import LegacyButton from '@atlaskit/button/standard-button';
import ChevronLeftIcon from '@atlaskit/icon/utility/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline, Stack, xcss } from '@atlaskit/primitives';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import Tooltip from '@atlaskit/tooltip';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { AI_MODAL_SCREEN_CLASS } from '../../../utils/constants';
import { ButtonWithShortcut } from '../../components/ButtonWithShortcut/ButtonWithShortcut';
import { CopyIconButton } from '../../components/CopyIconButton/CopyIconButton';
import { FeedbackForm } from '../../components/FeedbackForm/FeedbackForm';
import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';
import { defaultPaddingStyles } from '../../components/FloatingContainer/styles';
import { Footer } from '../../components/Footer/Footer';
import { FooterOld } from '../../components/Footer/FooterOld';
import { LatestPromptHeader } from '../../components/LatestPromptHeader/LatestPromptHeader';
import { PromptForm } from '../../components/PromptForm/PromptForm';
import { RetryIconButton } from '../../components/RetryIconButton/RetryIconButton';
import { useElementBreakpoints } from '../../hooks/useElementBreakpoints';
import { buttonAutoFocusRingFix, forceFocusRingStyles } from '../../styles/focus';
import { layoutBorderStyles } from '../../styles/renderer';
import { ActionButtonsLayout } from './components/ActionButtonsLayout';
import messages from './messages';
import { actionsButtonGroup, actionsContainer, actionsContainerNoPaddingTop, arrowContainer, columnLayout, compactActionButtonsStyles, containerLayout, multipromptContent, previewContainer, previewContainerWithMultiPrompt, previewScreenWithMultiPrompt } from './styles';
var compactSecondaryButtonGroupSmall = xcss({
  paddingTop: 'space.100'
});
export var CompactActionsBreakpoint = 480;

// Copied from packages/editor/editor-plugin-ai/src/config-items/config-items.tsx

/**
 * Could be either:
 * - userInput only (free form generate / interrogate)
 * - presetTitle only (summarize)
 * - both presetTitle and userInput (brainstorm)
 */

var mapActionAppearanceToButtonAppearance = {
  primary: 'primary',
  secondary: 'default',
  cancel: 'subtle'
};
export var PreviewScreen = function PreviewScreen(_ref) {
  var parentPresetPromptLabel = _ref.parentPresetPromptLabel,
    childShortTitle = _ref.childShortTitle,
    presetPromptLabel = _ref.presetPromptLabel,
    latestPrompt = _ref.latestPrompt,
    preview = _ref.preview,
    _ref$disableActions = _ref.disableActions,
    disableActions = _ref$disableActions === void 0 ? false : _ref$disableActions,
    _ref$disableInterroga = _ref.disableInterrogation,
    disableInterrogation = _ref$disableInterroga === void 0 ? false : _ref$disableInterroga,
    onSubmit = _ref.onSubmit,
    onCancel = _ref.onCancel,
    onCopy = _ref.onCopy,
    onRetry = _ref.onRetry,
    onInputFocus = _ref.onInputFocus,
    onInputBlur = _ref.onInputBlur,
    onInputChange = _ref.onInputChange,
    onADFChange = _ref.onADFChange,
    onGoodResponseButtonClick = _ref.onGoodResponseButtonClick,
    onBadResponseButtonClick = _ref.onBadResponseButtonClick,
    onEditPromptClick = _ref.onEditPromptClick,
    onBack = _ref.onBack,
    applications = _ref.applications,
    _ref$secondaryActions = _ref.secondaryActions,
    secondaryActions = _ref$secondaryActions === void 0 ? [] : _ref$secondaryActions,
    isInputActive = _ref.isInputActive,
    inputValue = _ref.inputValue,
    inputADFValue = _ref.inputADFValue,
    PromptEditor = _ref.PromptEditor,
    clearInputRef = _ref.clearInputRef,
    additionalFooterText = _ref.additionalFooterText,
    providerFactory = _ref.providerFactory,
    schema = _ref.schema,
    _ref$actionButtonsLay = _ref.actionButtonsLayoutMode,
    actionButtonsLayoutMode = _ref$actionButtonsLay === void 0 ? 'standard' : _ref$actionButtonsLay,
    footerRight = _ref.footerRight,
    disclaimer = _ref.disclaimer,
    agent = _ref.agent,
    multiPrompts = _ref.multiPrompts,
    onMultiPromptClick = _ref.onMultiPromptClick,
    multiPromptPresetTitle = _ref.multiPromptPresetTitle,
    multiPromptIndex = _ref.multiPromptIndex,
    isMultiPromptLoading = _ref.isMultiPromptLoading;
  var _useElementBreakpoint = useElementBreakpoints(CompactActionsBreakpoint),
    _useElementBreakpoint2 = _slicedToArray(_useElementBreakpoint, 2),
    setBreakpointsElement = _useElementBreakpoint2[0],
    isSmall = _useElementBreakpoint2[1].isSmall;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    forceFocusRing = _useState2[0],
    setForceFocusRing = _useState2[1];
  var firePrimaryApplication = function firePrimaryApplication() {
    if (!editorExperiment('platform_editor_ai_command_palette_post_ga', 'test') || !applications) {
      return;
    }
    setForceFocusRing(false);
    applications[applications.length - 1].onClick();
  };
  var applyButtonFocusStyles = useCallback(function (index) {
    return editorExperiment('platform_editor_ai_command_palette_post_ga', 'test') && index === applications.length - 1 && forceFocusRing ?
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
    forceFocusRingStyles :
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
    buttonAutoFocusRingFix;
  }, [forceFocusRing, applications.length]);
  var focusInputRef = useRef(undefined);
  var initialPlaceholder = agent ? formatMessage(messages.placeholderWithAgent, {
    agentName: agent.name
  }) : formatMessage(messages.defaultInputPlaceholder);
  var enableLinksInPromptEditor = !!fg('platform_editor_ai_interrogate_with_convo_ai');
  var actionButtons = useMemo(function () {
    if (!editorExperiment('platform_editor_ai_command_palate_improvement', 'test')) {
      return null;
    }
    var secondaryButtons = !disableActions && jsx(React.Fragment, null, actionButtonsLayoutMode === 'compact' && jsx(FeedbackForm, {
      onBadResponseButtonClick: onBadResponseButtonClick,
      onGoodResponseButtonClick: onGoodResponseButtonClick,
      iconsOnly: true,
      pressedIconsColor: "var(--ds-link-pressed, #0055CC)"
    }), onRetry && jsx(RetryIconButton, {
      spacing: actionButtonsLayoutMode === 'compact' ? 'compact' : undefined,
      appearance: actionButtonsLayoutMode === 'compact' ? 'subtle' : undefined,
      onClick: onRetry
    }), jsx(CopyIconButton, {
      spacing: actionButtonsLayoutMode === 'compact' ? 'compact' : undefined,
      appearance: actionButtonsLayoutMode === 'compact' ? 'subtle' : undefined,
      onClick: onCopy
    }), actionButtonsLayoutMode === 'compact' && secondaryActions.map(function (action) {
      var _action$analyticsTitl;
      var id = kebabCase((_action$analyticsTitl = action.analyticsTitle) !== null && _action$analyticsTitl !== void 0 ? _action$analyticsTitl : action.label);
      return jsx(Tooltip, {
        key: id,
        content: action.label,
        position: "top"
      }, jsx(IconButton, {
        testId: id,
        type: "button",
        onClick: action.onClick,
        icon: action.icon,
        label: action.label,
        appearance: "subtle",
        spacing: "compact"
      }));
    }));
    var cancelButton = jsx(LegacyButton, {
      testId: actionButtonsLayoutMode === 'compact' ? 'discard-button' : 'cancel-button',
      onClick: onCancel,
      type: "button",
      appearance: disableActions ? 'primary' : 'subtle',
      autoFocus: disableActions || applications.length === 0
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/no-unsafe-style-overrides -- Ignored via go/DSP-18766
      ,
      css: buttonAutoFocusRingFix
    }, actionButtonsLayoutMode === 'compact' ? formatMessage(messages.discardLabel) : formatMessage(messages.cancelLabel));
    var primaryButtons = !disableActions && applications.map(function (_ref2, index) {
      var onClick = _ref2.onClick,
        label = _ref2.label,
        appearance = _ref2.appearance,
        shortcut = _ref2.shortcut;
      return jsx(ButtonWithShortcut, {
        key: label,
        onClick: onClick,
        type: "button",
        shortcut: shortcut,
        autoFocus: appearance === 'primary',
        css: applyButtonFocusStyles(index),
        appearance: mapActionAppearanceToButtonAppearance[appearance]
      }, label);
    });
    return jsx(ActionButtonsLayout, {
      mode: actionButtonsLayoutMode,
      cancelButton: cancelButton,
      secondaryButtons: secondaryButtons,
      primaryButtons: primaryButtons
    });
  }, [actionButtonsLayoutMode, applyButtonFocusStyles, applications, disableActions, formatMessage, onBadResponseButtonClick, onCancel, onCopy, onGoodResponseButtonClick, onRetry, secondaryActions]);
  if (!editorExperiment('platform_editor_ai_command_palate_improvement', 'test')) {
    var legacyCancelButton = jsx(LegacyButton, {
      testId: "cancel-button",
      onClick: onCancel,
      type: "button",
      appearance: disableActions ? 'primary' : 'subtle',
      autoFocus: disableActions || applications.length === 0
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/no-unsafe-style-overrides -- Ignored via go/DSP-18766
      ,
      css: buttonAutoFocusRingFix
    }, formatMessage(messages.cancelLabel));
    if (actionButtonsLayoutMode === 'standard') {
      actionButtons =
      // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
      jsx("div", {
        css: actionsContainer
      }, legacyCancelButton, disableActions ? null :
      // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
      jsx("div", {
        css: actionsButtonGroup
      }, onRetry && jsx(RetryIconButton, {
        onClick: onRetry
      }), jsx(CopyIconButton, {
        onClick: onCopy
      }), applications.map(function (_ref3, index) {
        var onClick = _ref3.onClick,
          label = _ref3.label,
          appearance = _ref3.appearance;
        return jsx(LegacyButton, {
          key: label,
          testId: "insert-response-button",
          onClick: onClick,
          type: "button",
          autoFocus: appearance === 'primary'
          // eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
          ,
          css: applyButtonFocusStyles(index),
          appearance: mapActionAppearanceToButtonAppearance[appearance]
        }, label);
      })));
    }
    if (actionButtonsLayoutMode === 'compact') {
      var secondaryButtons = jsx(Inline, {
        alignBlock: "center",
        alignInline: isSmall ? 'end' : undefined,
        space: "space.100",
        xcss: [isSmall && compactSecondaryButtonGroupSmall]
      }, jsx(FeedbackForm, {
        onBadResponseButtonClick: onBadResponseButtonClick,
        onGoodResponseButtonClick: onGoodResponseButtonClick,
        iconsOnly: true,
        pressedIconsColor: "var(--ds-link-pressed, #0055CC)"
      }), onRetry && jsx(RetryIconButton, {
        spacing: "compact",
        appearance: "subtle",
        onClick: onRetry
      }), jsx(CopyIconButton, {
        spacing: "compact",
        appearance: "subtle",
        onClick: onCopy
      }), secondaryActions.map(function (action) {
        var _action$analyticsTitl2;
        var id = kebabCase((_action$analyticsTitl2 = action.analyticsTitle) !== null && _action$analyticsTitl2 !== void 0 ? _action$analyticsTitl2 : action.label);
        return jsx(Tooltip, {
          key: id,
          content: action.label,
          position: "top"
        }, jsx(IconButton, {
          testId: id,
          type: "button",
          onClick: action.onClick,
          icon: action.icon,
          label: action.label,
          appearance: "subtle",
          spacing: "compact"
        }));
      }));
      var primaryButtons =
      // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
      jsx("div", {
        css: [actionsContainer, actionsContainerNoPaddingTop]
      }, jsx(LegacyButton, {
        testId: "discard-button",
        onClick: onCancel,
        type: "button",
        appearance: "subtle",
        autoFocus: true
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/no-unsafe-style-overrides -- Ignored via go/DSP-18766
        ,
        css: buttonAutoFocusRingFix
      }, formatMessage(messages.discardLabel)), disableActions ? null :
      // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
      jsx("div", {
        css: actionsButtonGroup
      }, applications.map(function (_ref4, index) {
        var onClick = _ref4.onClick,
          label = _ref4.label,
          appearance = _ref4.appearance;
        return jsx(LegacyButton, {
          key: label,
          testId: "insert-response-button",
          onClick: onClick,
          type: "button",
          autoFocus: appearance === 'primary'
          // eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
          ,
          css: applyButtonFocusStyles(index),
          appearance: mapActionAppearanceToButtonAppearance[appearance]
        }, label);
      })));
      actionButtons =
      // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
      jsx("div", {
        css: compactActionButtonsStyles,
        ref: setBreakpointsElement
      }, isSmall ? jsx(React.Fragment, null, primaryButtons, secondaryButtons) : jsx(Inline, {
        alignBlock: "center",
        spread: "space-between"
      }, secondaryButtons, primaryButtons));
    }
  }
  var footerRightContent;
  if (footerRight) {
    footerRightContent = footerRight;
  } else {
    footerRightContent = actionButtonsLayoutMode === 'standard' ? jsx(FeedbackForm, {
      onGoodResponseButtonClick: onGoodResponseButtonClick,
      onBadResponseButtonClick: onBadResponseButtonClick
    }) : null;
  }
  return jsx("div", {
    /* eslint-disable @atlaskit/ui-styling-standard/no-classname-prop, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */
    css: [columnLayout, editorExperiment('platform_editor_ai_ai_button_block_elements', 'test') && layoutBorderStyles],
    className: "preview-screen"
    /* eslint-enable */
  }, jsx(FloatingContainer, {
    disablePadding: true,
    "data-testid": "preview-screen"
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: "preview-screen ".concat(AI_MODAL_SCREEN_CLASS)
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    ,
    css: containerLayout,
    rainbowBorder: !editorExperiment('platform_editor_ai_command_palette_post_ga', 'test')
  }, jsx(VisuallyHidden, null, formatMessage(messages.previewScreenLabel)), jsx(LatestPromptHeader, {
    parentPresetPromptLabel: parentPresetPromptLabel,
    childShortTitle: childShortTitle,
    prompt: latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.userInput,
    promptADF: latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.userADFInput,
    presetTitle: multiPromptPresetTitle !== null && multiPromptPresetTitle !== void 0 ? multiPromptPresetTitle : latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.presetTitle,
    agent: agent,
    providerFactory: providerFactory,
    schema: schema,
    onEditPromptClick: onEditPromptClick,
    showBack: true,
    onBack: onBack
  }), multiPrompts ?
  // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values
  jsx("div", {
    css: [previewContainerWithMultiPrompt, defaultPaddingStyles]
  }, jsx("div", {
    css: [previewScreenWithMultiPrompt]
  }, jsx("div", {
    css: arrowContainer
  }, jsx(IconButton, {
    type: "button",
    onClick: onMultiPromptClick ? function () {
      return onMultiPromptClick(-1);
    } : undefined,
    icon: ChevronLeftIcon,
    label: 'left',
    appearance: "subtle",
    spacing: "compact",
    isDisabled: isMultiPromptLoading
  })), jsx("div", {
    css: [multipromptContent]
  }, jsx(Stack, actionButtonsLayoutMode === 'compact' && {
    space: 'space.200'
  }, multiPrompts[multiPromptIndex].content)), jsx("div", {
    css: arrowContainer
  }, jsx(IconButton, {
    type: "button",
    onClick: onMultiPromptClick ? function () {
      return onMultiPromptClick(1);
    } : undefined,
    icon: ChevronRightIcon,
    label: 'right',
    appearance: "subtle",
    spacing: "compact",
    isLoading: isMultiPromptLoading
  }))), jsx("div", null, actionButtons)) :
  // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values
  jsx("div", {
    css: [previewContainer, defaultPaddingStyles]
  }, jsx(Stack, actionButtonsLayoutMode === 'compact' && {
    space: 'space.200'
  }, preview, actionButtons)), disableActions || disableInterrogation ? null :
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
  jsx("div", {
    css: defaultPaddingStyles
  }, jsx(PromptForm, {
    showButtons: isInputActive,
    autoFocus: false,
    placeholder: initialPlaceholder,
    ariaLabel: initialPlaceholder,
    value: inputValue,
    adfValue: inputADFValue,
    onFormSubmit: onSubmit,
    onCancel: onCancel,
    onInputChange: onInputChange,
    onADFChange: onADFChange,
    onInputFocus: onInputFocus,
    onInputBlur: onInputBlur,
    focusInputRef: focusInputRef,
    clearInputRef: clearInputRef,
    PromptEditor: PromptEditor,
    enableLinks: enableLinksInPromptEditor,
    fireParentActionButton: firePrimaryApplication,
    focusParentActionButton: setForceFocusRing
  })), editorExperiment('platform_editor_ai_command_palette_post_ga', 'test') ? jsx(Footer, {
    brand: agent ? 'rovo' : 'atlassian-intelligence',
    disclaimer: disclaimer,
    showDisclaimer: true
  }) : jsx(FooterOld, {
    footerRightContent: footerRightContent,
    disclaimer: disclaimer,
    additionalFooterText: additionalFooterText
  })));
};